import ApiErrorBanner from '@/components/ApiErrorBanner';
import { errorToast, successToast } from '@/helpers.tsx';
import { useApiHook } from '@/support/Hooks/api/useApiHook.ts';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import { ErrorMessage } from '@hookform/error-message';
import { type FC } from 'react';
import { useForm } from 'react-hook-form';
import { type StudyApiId } from '@/model/Study.model.ts';
import { type ExportApiId } from '@/components/Studies/StudyExports/ExportsList/Exports.model.ts';

export interface RenameExportProps {
  studyApiId: StudyApiId;
  exportApiId: ExportApiId;
  currentFileName: string | null;
  onRenamed: () => void | Promise<void>;
  closeModal: () => void;
}

interface FormValues {
  file_name: string;
}

const RenameExport: FC<RenameExportProps> = (props) => (
  <ModalContainer size="narrow">
    <ModalHeader title="Rename export" closeModal={props.closeModal} className="pa3 bb b--moon-gray bg-white" />
    <RenameExportForm {...props} />
  </ModalContainer>
);

const RenameExportForm: FC<RenameExportProps> = ({
  studyApiId,
  exportApiId,
  currentFileName,
  onRenamed,
  closeModal,
}) => {
  const {
    invoke: patchExport,
    response: exportPATCHResponse,
    loading: patchingExport,
  } = useApiHook({
    endpoint: 'PATCH /api/v1/studies/{studyApiId}/exports/{exportApiId}',
    options: {
      invokeOnInit: false,
      onError: { slug: 'rename-export', toast: false },
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: { file_name: currentFileName ?? '' },
  });

  const onSubmit = async ({ file_name }: FormValues): Promise<void> => {
    try {
      await patchExport({
        path: { studyApiId, exportApiId },
        body: { file_name: file_name.trim() },
      });
    } catch {
      errorToast('Could not rename export');
      return;
    }
    await onRenamed();
    closeModal();
    successToast('Export renamed');
  };

  return (
    <form>
      {exportPATCHResponse?.type === 'error' && <ApiErrorBanner />}
      <div className="pa3">
        <label htmlFor="file_name">File name</label>
        <input
          type="text"
          id="file_name"
          data-testid="rename-export__input"
          style={{ marginBottom: 0 }}
          {...register('file_name', {
            required: 'File name is required',
            maxLength: { value: 255, message: 'Maximum of 255 characters' },
            validate: {
              noSeparators: (value) => !/[\\/]/.test(value) || 'File name cannot contain / or \\',
            },
          })}
          autoFocus
        />
        <ErrorMessage
          errors={errors}
          name="file_name"
          render={({ message }) => <small className="red db pt2">{message}</small>}
        />
      </div>
      <ModalActions
        submitButtonProps={{ disabled: patchingExport }}
        onSubmit={handleSubmit(onSubmit)}
        onCancel={closeModal}
        className="pa3 bt b--moon-gray"
      />
    </form>
  );
};

export default RenameExport;
